import React, { createContext, useContext, useState, useEffect } from 'react';

// 创建主题上下文
export const ThemeContext = createContext();

/**
 * 主题提供者组件
 * @param {Object} props
 * @param {ReactNode} props.children - 子组件
 */
export const ThemeProvider = ({ children }) => {
  // 主题状态: system(跟随系统), dark(暗面), light(亮面)
  const [theme, setTheme] = useState(() => {
    // 从本地存储获取主题设置，默认为"system"
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'system';
  });

  // 是否为暗色主题
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (theme === 'dark') return true;
    if (theme === 'light') return false;
    // system theme
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // 侧边栏展开状态，默认为展开
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    // 从本地存储获取侧边栏状态，默认为展开
    const savedState = localStorage.getItem('sidebarExpanded');
    return savedState !== null ? JSON.parse(savedState) : true;
  });

  // 当主题变化时更新DOM和本地存储
  useEffect(() => {
    // 保存到本地存储
    localStorage.setItem('theme', theme);

    // 根据主题设置应用相应的类
    if (theme === 'system') {
      // 跟随系统
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
      setIsDarkMode(prefersDark);
    } else {
      // 手动设置
      document.documentElement.setAttribute('data-theme', theme);
      setIsDarkMode(theme === 'dark');
    }
  }, [theme]);

  // 当侧边栏状态变化时保存到本地存储
  useEffect(() => {
    localStorage.setItem('sidebarExpanded', JSON.stringify(sidebarExpanded));
  }, [sidebarExpanded]);

  // 添加系统主题变化监听
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      if (theme === 'system') {
        document.documentElement.setAttribute(
          'data-theme', 
          e.matches ? 'dark' : 'light'
        );
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      setTheme, 
      isDarkMode, 
      sidebarExpanded, 
      setSidebarExpanded 
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

// 自定义Hook，方便组件使用主题上下文
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme 必须在 ThemeProvider 内部使用');
  }
  return context;
}; 